import { useState } from 'react';
import { useDeleteAgentVersion } from '../../hooks/useAgents';
import { Badge, Button, Card, CardHeader, ConfirmModal, Modal } from '@/components/ui';
import { useToast } from '../ui/Toast';
import type { AgentVersion } from '../../types';

interface VersionHistoryProps {
  agentId: string;
  versions: AgentVersion[];
  currentVersion?: number;
}

export default function VersionHistory({
  agentId,
  versions,
  currentVersion,
}: VersionHistoryProps) {
  const { success, error: showError } = useToast();
  const deleteVersion = useDeleteAgentVersion();

  const [selectedVersion, setSelectedVersion] = useState<AgentVersion | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    version: AgentVersion | null;
  }>({ isOpen: false, version: null });

  const handleDelete = async () => {
    if (!deleteModal.version) return;

    try {
      await deleteVersion.mutateAsync({
        agentId,
        version: deleteModal.version.version,
      });
      success('Version deleted successfully');
      setDeleteModal({ isOpen: false, version: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete version');
    }
  };

  const sortedVersions = [...versions]
    .filter((v) => v.version != null)
    .sort((a, b) => b.version - a.version);

  return (
    <>
      <Card>
        <CardHeader
          title="Version History"
          description="Published versions of this agent"
        />
        {sortedVersions.length === 0 ? (
          <p className="text-muted-foreground">
            No versions published yet. Publish a version to create a snapshot.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {sortedVersions.map((version) => (
              <div
                key={version.version}
                className="py-4 first:pt-0 last:pb-0 flex items-center justify-between"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">
                      Version {version.version}
                    </span>
                    {version.version === currentVersion && (
                      <Badge variant="success">Latest</Badge>
                    )}
                  </div>
                  {version.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {version.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Published {new Date(version.publishedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedVersion(version)}
                  >
                    View
                  </Button>
                  {version.version !== currentVersion && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteModal({ isOpen: true, version })}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Version details modal */}
      {selectedVersion && (
        <Modal
          isOpen={!!selectedVersion}
          onClose={() => setSelectedVersion(null)}
          title={`Version ${selectedVersion.version}`}
          size="lg"
        >
          <div className="space-y-4">
            {selectedVersion.description && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                <p className="mt-1 text-foreground">{selectedVersion.description}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">Published</h4>
              <p className="mt-1 text-foreground">
                {new Date(selectedVersion.publishedAt).toLocaleString()}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted-foreground">Configuration</h4>
              <pre className="mt-1 text-sm bg-muted p-4 rounded-md overflow-x-auto max-h-96">
                {JSON.stringify(selectedVersion.snapshot, null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, version: null })}
        onConfirm={handleDelete}
        title="Delete Version"
        message={`Are you sure you want to delete version ${deleteModal.version?.version}? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteVersion.isPending}
      />
    </>
  );
}
